import { useState, useEffect, useCallback } from 'react';
import { api, useAuthStore } from '../features/auth/authStore';

interface SessionInfo {
  hasSession: boolean;
  timeUntilExpiry: number | null;
  shouldShowWarning: boolean;
  sessionTimeout: number | null;
  warningTime: number | null;
  lastActivity?: string;
  rememberMe?: boolean;
}

interface SessionSettings {
  warningTime: number;
  rememberMeEnabled: boolean;
  rememberMeDuration: number;
  activityExtensionEnabled: boolean;
  roleTimeouts: Record<string, number>;
}

export const useSessionManager = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuthStore();
  const [sessionInfo, setSessionInfo] = useState<SessionInfo>({
    hasSession: false,
    timeUntilExpiry: null,
    shouldShowWarning: false,
    sessionTimeout: null,
    warningTime: null
  });

  const [sessionSettings, setSessionSettings] = useState<SessionSettings | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch session information
  const fetchSessionInfo = useCallback(async () => {
    // Only fetch session info if user is authenticated and auth is not loading
    if (!isAuthenticated || authLoading) {
      setSessionInfo({
        hasSession: false,
        timeUntilExpiry: null,
        shouldShowWarning: false,
        sessionTimeout: null,
        warningTime: null
      });
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/session/info');
      if (response.data.success) {
        const info = response.data.data;
        setSessionInfo(info);

        // Show warning if needed and not already showing
        if (info.shouldShowWarning && !showWarning) {
          setShowWarning(true);
        }

        // Hide warning if no longer needed
        if (!info.shouldShowWarning && showWarning) {
          setShowWarning(false);
        }
      }
    } catch (error) {
      console.error('Failed to fetch session info:', error);
      // If session info fails, assume no session
      setSessionInfo({
        hasSession: false,
        timeUntilExpiry: null,
        shouldShowWarning: false,
        sessionTimeout: null,
        warningTime: null
      });
    } finally {
      setLoading(false);
    }
  }, [showWarning, isAuthenticated, authLoading]);

  // Fetch session settings
  const fetchSessionSettings = useCallback(async () => {
    // Only fetch session settings if user is authenticated and auth is not loading
    if (!isAuthenticated || authLoading) {
      return;
    }

    try {
      const response = await api.get('/session/settings');
      if (response.data.success) {
        setSessionSettings(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch session settings:', error);
    }
  }, [isAuthenticated, authLoading]);

  // Extend session
  const extendSession = useCallback(async () => {
    try {
      const response = await api.post('/session/extend');
      if (response.data.success) {
        await fetchSessionInfo(); // Refresh session info
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to extend session:', error);
      return false;
    }
  }, [fetchSessionInfo]);

  // Get user sessions
  const getUserSessions = useCallback(async () => {
    try {
      const response = await api.get('/session/user-sessions');
      if (response.data.success) {
        return response.data.data.sessions;
      }
      return [];
    } catch (error) {
      console.error('Failed to get user sessions:', error);
      return [];
    }
  }, []);

  // Terminate a session
  const terminateSession = useCallback(async (sessionId: string) => {
    try {
      const response = await api.delete(`/session/${sessionId}`);
      return response.data.success;
    } catch (error) {
      console.error('Failed to terminate session:', error);
      return false;
    }
  }, []);

  // Handle warning modal actions
  const handleWarningClose = useCallback(() => {
    setShowWarning(false);
  }, []);

  const handleWarningExtend = useCallback(async () => {
    const success = await extendSession();
    if (success) {
      setShowWarning(false);
    }
  }, [extendSession]);

  // Set up polling for session info
  useEffect(() => {
    // Only fetch session info and settings if user is authenticated and auth is not loading
    if (isAuthenticated && !authLoading) {
      fetchSessionInfo();
      fetchSessionSettings();

      // Poll session info every 30 seconds
      const interval = setInterval(fetchSessionInfo, 30000);
      return () => clearInterval(interval);
    } else {
      // If not authenticated, just set loading to false
      setLoading(false);
    }
  }, [fetchSessionInfo, fetchSessionSettings, isAuthenticated, authLoading]);

  // Format time for display
  const formatTimeLeft = useCallback((minutes: number | null) => {
    if (!minutes || minutes < 1) return 'less than 1 minute';
    if (minutes === 1) return '1 minute';
    if (minutes < 60) return `${minutes} minutes`;
    
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 1 && mins === 0) return '1 hour';
    if (mins === 0) return `${hours} hours`;
    return `${hours}h ${mins}m`;
  }, []);

  return {
    sessionInfo,
    sessionSettings,
    showWarning,
    loading,
    extendSession,
    getUserSessions,
    terminateSession,
    handleWarningClose,
    handleWarningExtend,
    formatTimeLeft,
    refreshSessionInfo: fetchSessionInfo
  };
};
